import { useEffect, useState } from 'react';
import { getRedemptions } from '@/api/member';
import { MEMBER_HISTORY_PAGE_SIZE_MAX } from '@shared/input-validation';

export interface ClaimedTopics {
  ids: ReadonlySet<string>;
  // 没有主题 id 的旧记录按名称兜底
  names: ReadonlySet<string>;
}

const EMPTY: ClaimedTopics = { ids: new Set(), names: new Set() };

/** 按主题 id 判断是否领过，主题改名不影响；旧记录按名称兜底。 */
export function isTopicClaimed(claimed: ClaimedTopics, topic: { id: string; topic_name: string }): boolean {
  return claimed.ids.has(topic.id) || claimed.names.has(topic.topic_name);
}

/**
 * 当前会员已成功领取过的主题集合，用于列表“已领取”标记和“只看未领取”。
 * 复用会员自己的领取记录接口，登录后取一次；领取成功会跳到成功页，
 * 返回列表时页面重新挂载，自然会再取一次最新记录。
 * 读取失败时静默返回空集合，不影响浏览和领取主流程。
 */
export function useClaimedTopics(passcode: string | null | undefined): ClaimedTopics {
  const [claimed, setClaimed] = useState<ClaimedTopics>(EMPTY);

  useEffect(() => {
    if (!passcode) {
      setClaimed(EMPTY);
      return;
    }

    let cancelled = false;
    getRedemptions({ passcode, pageSize: MEMBER_HISTORY_PAGE_SIZE_MAX })
      .then((result) => {
        if (cancelled) return;
        const ids = new Set<string>();
        const names = new Set<string>();
        for (const record of result.items) {
          if (record.record_type === 'quota_adjustment' || record.result !== 'success') continue;
          if (record.topic_id) {
            ids.add(record.topic_id);
          } else if (record.topic_name) {
            names.add(record.topic_name);
          }
        }
        setClaimed({ ids, names });
      })
      .catch(() => {
        if (!cancelled) setClaimed(EMPTY);
      });

    return () => {
      cancelled = true;
    };
  }, [passcode]);

  return claimed;
}
